import { useState } from "react";
import { Link } from "react-router-dom";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { Modal } from "../../components/Modal/Modal";
import { Select, type Option } from "../../components/Select/Select";
import { Textarea } from "../../components/Textarea/Textarea";
import { DialogRow } from "./DialogRow";
import { guildSchemaLink } from "./links";
import {
  columnID,
  isNumberType,
  numberError,
  MAX_NAME_RUNES,
  NOT_GIVEN,
  type CardKey,
} from "./roster";
import type { GuildTemplate, GuildTemplateInput } from "./types";
import styles from "./GuildTemplatesTab.module.css";

/**
 * One guild template, typed by hand — the way a name is corrected without
 * exporting a CSV, changing one cell and importing it back.
 *
 * The fields are exactly the file's: name, description, logo, and one box per
 * key of `guild.metadata_schema`. They are the four the PATCH replaces, and all
 * four travel every time — «Save template» means what is on the screen, so a
 * request that omitted the metadata would be the browser keeping a value the
 * operator had just cleared.
 *
 * That is also the whole of what makes the Logo row differ from the identical
 * field on the guild's own settings form: a dialog that sends every field needs
 * «no logo» to be a state an operator can PICK, so it is the first entry of the
 * list. And a logo outside the project's whitelist stays selected, with a mark
 * on it — the whitelist is a hint no server checks, so the alternative is a
 * dialog opened to fix a description silently stripping a logo that arrived in
 * a file.
 *
 * Values are held as TEXT while the dialog is open and turned into JSON on the
 * way out, which is what the CSV reader does with a cell and for the same
 * reason: an empty box is «this key is not given», never a zero and never an
 * empty string. The row editor on the Source tab holds a card the same way.
 *
 * A SPENT row never reaches this dialog — the server refuses to rewrite one,
 * and the row menu of a spent row offers no edit. What does reach it is a
 * BLOCKED row, and renaming it is the whole point: a guild of this project
 * carries that name today.
 *
 * With `row: null` it CREATES, and that is now reached from an empty form as
 * well as from «Duplicate»: the toolbar and the empty table both open it, so a
 * project's first template no longer has to arrive through a CSV file.
 *
 * Mockups: t-103/bots-guild-templates.html §1, t-108/bots-guild-template-create.html §3–4,
 * t-115/bot-guild-template-logo.html §2–4.
 */

/** What the admin guild editor holds a description to, and therefore what the
 *  importer and this dialog hold it to. */
const MAX_DESCRIPTION_RUNES = 512;

/** A draft value: text for every type this schema can declare. */
type DraftValue = string;

interface Props {
  /** The project whose schema the boxes are, and whose Config page the empty
   *  one links to. */
  projectID: string;
  /** The row being edited; null when this dialog will create one. */
  row: GuildTemplate | null;
  /** What the boxes open with — the row's own values, or a copy of them. */
  initial: GuildTemplateInput;
  title: string;
  /** `guild.metadata_schema`, as this page's card keys. */
  metaKeys: CardKey[];
  /**
   * `games.config.guild_assets.allowed_logos` — the asset ids this project's
   * logo field offers, exactly as the guild's own settings form takes them.
   * Empty means the project declared none and the field is free text: the list
   * is a hint for the operator, not a rule any server keeps, so an empty one
   * must not lock the field.
   */
  allowedLogos: string[];
  /**
   * The names of the OTHER rows this page has loaded, so a clash can be named
   * before the server names it. It is what this page KNOWS, not what is true of
   * the whole table — see the hint under the name box.
   *
   * UNIQUENESS IS THE SERVER'S. Fifty rows out of thousands cannot answer the
   * question, so a duplicate the page never loaded comes back as a 409
   * `name_taken` and is printed in the alert at the top of this dialog.
   */
  takenNames: string[];
  saving: boolean;
  /** What the last save was refused for, already turned into a sentence. */
  error: string | null;
  onClose: () => void;
  onSave: (input: GuildTemplateInput) => void;
}

export function EditGuildTemplateModal({
  projectID,
  row,
  initial,
  title,
  metaKeys,
  allowedLogos,
  takenNames,
  saving,
  error,
  onClose,
  onSave,
}: Props) {
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [logo, setLogo] = useState(initial.logo);
  const [values, setValues] = useState<Record<string, DraftValue>>(() =>
    draftOf(initial.metadata, metaKeys),
  );

  // A whitelist the project actually filled in turns the field into a picker;
  // an empty one leaves it free text — the guild settings form's own branch,
  // and the players page's avatar rule before it.
  const useLogoSelect = allowedLogos.length > 0;
  // A logo the whitelist does not list. It is legal — nothing checks a logo
  // against that list, here or on a guild — so it is shown, marked, and saved
  // as it stands rather than quietly dropped.
  const logoOrphan = useLogoSelect && logo !== "" && !allowedLogos.includes(logo);

  const trimmed = name.trim();
  // EXACTLY, because `bot_guild_templates_name_uniq` is `UNIQUE (game_id,
  // name)` with no `lower()` in it and every comparison behind it is byte for
  // byte. «Iron Wolves» beside «iron wolves» is two lawful rows, and refusing
  // the second stated something untrue about the table.
  const nameTaken = takenNames.some((n) => n === trimmed);
  // A clash of case alone is worth SAYING and not worth refusing: it will
  // save, and two rows a human reads as one name is the sort of thing an
  // operator wants to be told about while they can still change their mind.
  const caseClash =
    !nameTaken &&
    trimmed !== "" &&
    takenNames.some((n) => n.toLowerCase() === trimmed.toLowerCase());
  // A row the sweep blocked is blocked over the name it still carries. Once
  // that name is changed the reason is a statement about a value that is no
  // longer there, so the hint goes with it.
  const blockedOnThisName =
    row?.blocked_reason === "name_taken" && trimmed === initial.name.trim();

  const tooLong = [...trimmed].length > MAX_NAME_RUNES;
  const descTooLong = [...description].length > MAX_DESCRIPTION_RUNES;
  const badNumbers = metaKeys.filter((k) => numberError(k, values[columnID(k)]));
  const nameError = tooLong
    ? `A name is at most ${MAX_NAME_RUNES} characters.`
    : nameTaken
      ? "Another template of this table already carries this name."
      : null;
  const blocked =
    trimmed === "" || !!nameError || descTooLong || badNumbers.length > 0;

  function save() {
    if (blocked) return;
    onSave({
      name: trimmed,
      description: description.trim(),
      logo: logo.trim(),
      metadata: metadataOf(metaKeys, values),
    });
  }

  return (
    <Modal
      open
      width="lg"
      title={title}
      onClose={onClose}
      closeDisabled={saving}
      footer={
        <>
          {/* The table is written the moment this closes — there is no save
              bar behind it, and a dialog that did not say so would leave the
              operator looking for one. */}
          <span className={styles.footNote}>Saved immediately.</span>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={blocked} loading={saving}>
            {row ? "Save template" : "Add template"}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" title="This template was refused">
          {error}
        </Alert>
      )}

      <DialogRow label="Name">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Name"
          autoFocus
          error={nameError ?? undefined}
        />
        {!nameError && (
          <div
            className={
              blockedOnThisName
                ? `${styles.helper} ${styles.helperBad}`
                : styles.helper
            }
          >
            {blockedOnThisName
              ? "A guild of this project already carries this name — rename the row to unblock it."
              : caseClash
                ? "Another row on this page carries this name in a different case. Names are case-sensitive, so both are allowed — this one will save."
                : "Free — no other row on this page carries it. The server checks the whole table and the project's guilds when the template is saved."}
          </div>
        )}
      </DialogRow>

      <DialogRow label="Description">
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          aria-label="Description"
          rows={3}
          error={
            descTooLong
              ? `A description is at most ${MAX_DESCRIPTION_RUNES} characters.`
              : undefined
          }
        />
        <div className={styles.helper}>
          What a living player reads before joining. It is written to the guild
          as it stands here.
        </div>
      </DialogRow>

      {/* Logo is a field of the GUILD, like the name and the description, and
          not a key of its schema — so it sits here, between the two of them
          and the schema's own boxes, and carries no mono sub-label: `api` in
          DialogRow names a config path the boxes below write INTO, and this
          one writes into the template's own column. */}
      <DialogRow label="Logo">
        <div className={styles.logoField}>
          {useLogoSelect ? (
            <Select
              aria-label="Logo"
              value={logo}
              onChange={setLogo}
              options={logoOptions(allowedLogos, logo)}
              monospaceValue
              className={`${styles.logoSelect} ${logoOrphan ? styles.selectWarn : ""}`}
            />
          ) : (
            <Input
              aria-label="Logo"
              value={logo}
              placeholder="e.g. emblem_wolf"
              className={styles.monoInput}
              onChange={(e) => setLogo(e.target.value)}
            />
          )}
          {!useLogoSelect ? (
            <div className={styles.helper}>
              The in-game asset the guild wears. Whitelist{" "}
              <code>allowed_logos</code> is empty → free-text input, any string
              accepted. Leave it empty and the guild is created without a logo.
            </div>
          ) : logoOrphan ? (
            <div className={styles.warnHelper}>
              Outside the project&apos;s allowed-logos whitelist. The whitelist
              is a hint, not a rule — this value is kept and saved as it stands.
            </div>
          ) : (
            <div className={styles.helper}>
              The in-game asset the guild wears. From the project&apos;s
              allowed-logos whitelist.
            </div>
          )}
        </div>
      </DialogRow>

      <DialogRow label="Metadata" api="guild.metadata_schema">
        {metaKeys.length > 0 ? (
          <>
            {metaKeys.map((k) => (
              <ValueRow
                key={columnID(k)}
                field={k}
                value={values[columnID(k)]}
                onChange={(next) =>
                  setValues((prev) => ({ ...prev, [columnID(k)]: next }))
                }
              />
            ))}
            {/* The block has always worked this way and never said so, and a
                column of boxes with no sentence under it reads as a set to
                choose from. There is nothing to choose: the schema IS the
                set. */}
            <div className={styles.helper}>
              Every key this project declares gets a box, and only those. Leave
              one alone and the key is simply absent from the guild — there is
              nothing to pick here.
            </div>
          </>
        ) : (
          <Alert variant="info" title="This project declares no guild metadata">
            <code>guild.metadata_schema</code> is empty, so a template carries a
            name and a description and nothing else. Declare the keys in{" "}
            {/* The dialog goes with the click — an ordinary navigation, and
                the draft on screen goes with it. That is the price of a link
                rather than a second schema editor in here, and it is payable
                because a schema is declared once per project. */}
            <Link to={guildSchemaLink(projectID)} onClick={onClose}>
              Config → Guild defaults → Metadata schema
            </Link>
            , then come back — the boxes here are that schema, one per key.
          </Alert>
        )}
      </DialogRow>
    </Modal>
  );
}

/**
 * What the logo picker offers: «no logo» first, then the project's whitelist,
 * then — only when the row carries one — the value that is in neither.
 *
 * The first entry is what this dialog needs and the guild's settings form does
 * not: every field goes with every save here, so «no logo» has to be something
 * an operator can choose. `NOT_GIVEN` is the word the metadata boxes below
 * already use for exactly that.
 *
 * The last is PlayerMetaEditor's `orphan`, with a different sentence beside it:
 * there an out-of-list value has to be replaced before the card will save,
 * here it is simply legal — no server checks a logo against the whitelist — so
 * it is marked rather than refused.
 */
function logoOptions(allowed: string[], current: string): Option[] {
  const orphan = current !== "" && !allowed.includes(current);
  return [
    { value: "", label: NOT_GIVEN, muted: true, separatorAfter: true },
    ...allowed.map((v, i) => ({
      value: v,
      label: v,
      mono: true,
      separatorAfter: orphan && i === allowed.length - 1,
    })),
    ...(orphan
      ? [{ value: current, label: current, mono: true, meta: "not in whitelist" }]
      : []),
  ];
}

/** One declared key: the box its type asks for, and «— not given —» wherever
 *  the key may simply be absent. */
function ValueRow({
  field,
  value,
  onChange,
}: {
  field: CardKey;
  value: DraftValue | undefined;
  onChange: (next: DraftValue) => void;
}) {
  const text = value ?? "";
  const err = numberError(field, value);
  return (
    <div className={styles.kvRow}>
      <span className={styles.kvKey}>{field.key}</span>
      <div className={styles.kvValue}>
        {field.type === "bool" ? (
          <Select
            aria-label={field.key}
            value={text}
            onChange={onChange}
            options={[
              { value: "", label: NOT_GIVEN, muted: true },
              { value: "true", label: "yes" },
              { value: "false", label: "no" },
            ]}
          />
        ) : field.allowed && field.allowed.length > 0 ? (
          <Select
            aria-label={field.key}
            value={text}
            onChange={onChange}
            options={[
              { value: "", label: NOT_GIVEN, muted: true },
              ...field.allowed.map((v) => ({ value: v, label: v })),
            ]}
          />
        ) : (
          <Input
            aria-label={field.key}
            value={text}
            placeholder="not given"
            inputMode={isNumberType(field) ? "numeric" : undefined}
            onChange={(e) => onChange(e.target.value)}
            error={err ?? undefined}
          />
        )}
      </div>
    </div>
  );
}

/* --- text in, JSON out ---------------------------------------------------- */

/** The row's stored metadata, as the boxes that show it. */
function draftOf(
  metadata: Record<string, unknown>,
  keys: CardKey[],
): Record<string, DraftValue> {
  const out: Record<string, DraftValue> = {};
  for (const k of keys) {
    const raw = metadata?.[k.key];
    if (raw === undefined || raw === null) {
      out[columnID(k)] = "";
      continue;
    }
    out[columnID(k)] =
      typeof raw === "object" ? JSON.stringify(raw) : String(raw);
  }
  return out;
}

/** …and back. A box left empty leaves its key out of the object entirely,
 *  which is the difference between «no minimum level» and «minimum level 0». */
function metadataOf(
  keys: CardKey[],
  values: Record<string, DraftValue>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    const text = (values[columnID(k)] ?? "").trim();
    if (text === "") continue;
    if (k.type === "bool") {
      out[k.key] = text === "true";
      continue;
    }
    if (isNumberType(k)) {
      out[k.key] = Number(text);
      continue;
    }
    out[k.key] = text;
  }
  return out;
}
